// Cart drawer: opens from the header or after any add-to-cart. Server HTML (Section Rendering API) is morphed into the
// open drawer so nothing flashes: lines, packs and gifts grow in and collapse out, steppers answer instantly, and a
// pack that reaches a new offer tier gets a small confetti burst.
(() => {
  'use strict';

  const SECTION = 'cart-drawer';
  const root = window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(.2,.8,.2,1)';
  const SHAPE = 320;

  // ---- Morph: patch the live drawer to match fresh server HTML, keeping elements that persist. ----
  // Line keys change when Shopify reallocates a discount, so lines are keyed by variant + offer instance instead.
  const keyOf = (n) => (n.nodeType === 1 ? n.dataset.key || n.dataset.pack || n.dataset.line || n.id || null : null);
  const animated = (n) => n.nodeType === 1 && (n.dataset.line || n.dataset.pack || n.dataset.key);
  // Elements marked data-ui hold state the shopper controls (open, folded); the server must not reset it.
  const UI_ATTRS = ['aria-expanded', 'inert', 'hidden', 'aria-busy'];
  const UI_CLASSES = ['is-expanded', 'is-folded', 'is-open'];

  function grow(el) {
    if (reduced.matches) return;
    const height = el.getBoundingClientRect().height;
    el.animate([{ height: '0px', opacity: 0, overflow: 'hidden' }, { height: `${height}px`, opacity: 1, overflow: 'hidden' }], { duration: SHAPE, easing: EASE });
  }
  function collapse(el) {
    if ('leaving' in el.dataset) return;
    el.dataset.leaving = '';
    if (reduced.matches) { el.remove(); return; }
    const height = el.getBoundingClientRect().height;
    el.style.overflow = 'hidden';
    el.animate(
      [{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px' }],
      { duration: SHAPE, easing: EASE, fill: 'forwards' },
    ).onfinish = () => el.remove();
  }

  function syncAttributes(from, to) {
    const ui = from.hasAttribute('data-ui');
    for (const { name } of [...from.attributes]) {
      if (to.hasAttribute(name) || (ui && UI_ATTRS.includes(name))) continue;
      from.removeAttribute(name);
    }
    for (const { name, value } of [...to.attributes]) {
      if (ui && UI_ATTRS.includes(name)) continue;
      if (name === 'class' && ui) {
        const keep = UI_CLASSES.filter((c) => from.classList.contains(c));
        const next = value.split(/\s+/).filter((c) => c && !UI_CLASSES.includes(c)).concat(keep).join(' ');
        if (from.className !== next) from.className = next;
        continue;
      }
      if (from.getAttribute(name) !== value) from.setAttribute(name, value);
    }
  }

  function morph(from, to, entering) {
    if (from.nodeType === 3 || from.nodeType === 8) {
      if (from.data !== to.data) from.data = to.data;
      return;
    }
    syncAttributes(from, to);
    const current = [...from.childNodes].filter((n) => !(n.dataset && 'leaving' in n.dataset));
    const keyed = new Map(current.filter(keyOf).map((n) => [keyOf(n), n]));
    const used = new Set();
    const pairs = [...to.childNodes].map((next) => {
      const key = keyOf(next);
      let match = key ? keyed.get(key) : current.find((n) => !used.has(n) && !keyOf(n) && n.nodeType === next.nodeType && n.nodeName === next.nodeName);
      if (match && match.nodeName !== next.nodeName) match = null;
      if (match) used.add(match);
      return [match, next];
    });
    current.filter((n) => !used.has(n)).forEach((n) => (animated(n) ? collapse(n) : n.remove()));
    let ref = from.firstChild;
    const skip = () => { while (ref && ref.dataset && 'leaving' in ref.dataset) ref = ref.nextSibling; };
    for (const [match, next] of pairs) {
      skip();
      if (match) {
        if (match !== ref) from.insertBefore(match, ref);
        else ref = ref.nextSibling;
        morph(match, next, entering);
      } else {
        const node = document.importNode(next, true);
        from.insertBefore(node, ref);
        if (animated(node)) entering.push(node);
      }
    }
  }

  // ---- Confetti: a short brand-coloured burst from the strip that announces the new tier. ----
  function celebrate(el) {
    if (!el || reduced.matches) return;
    const box = el.getBoundingClientRect();
    const layer = Object.assign(document.createElement('div'), { className: 'confetti' });
    layer.ariaHidden = 'true';
    document.body.append(layer);
    const colors = ['#021550', '#afd8f2', '#0b7a75', '#f5a524', '#a298db', '#f0bbd3'];
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    for (let i = 0; i < 44; i++) {
      const bit = document.createElement('i');
      bit.style.left = `${cx + (Math.random() - 0.5) * box.width * 0.6}px`;
      bit.style.top = `${cy}px`;
      bit.style.background = colors[i % colors.length];
      if (i % 4 === 0) bit.classList.add('is-dot');
      layer.append(bit);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      const speed = 140 + Math.random() * 160;
      const dx = Math.cos(angle) * speed * 1.4;
      const dy = Math.sin(angle) * speed;
      const spin = (Math.random() - 0.5) * 900;
      bit.animate([
        { transform: 'translate(-50%,-50%) scale(.6) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) scale(1) rotate(${spin * 0.6}deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(calc(-50% + ${dx * 1.15}px),calc(-50% + ${dy + 160}px)) scale(.9) rotate(${spin}deg)`, opacity: 0 },
      ], { duration: 700 + Math.random() * 200, easing: 'cubic-bezier(.12,.75,.3,1)', fill: 'forwards' });
    }
    setTimeout(() => layer.remove(), 1000);
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.queue = Promise.resolve();
      this.pendingSteps = new Map(); // line element -> { timer, fire } while a stepper waits out its pause
      this.targets = new Map();      // line element -> quantity the shopper asked for, until the server has it
      this.removing = new Set();     // selectors of rows/packs being removed; kept hidden if a re-render brings them back
      this.folds = new WeakMap();    // pack -> timer that folds it back after showing newly added hoodies
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (!opener) return;
        e.preventDefault();
        this.open(opener);
      });
      addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      // Start loading the drawer's photos when a pointer or finger reaches the cart button, before the tap.
      const warm = (e) => {
        if (!e.target.closest?.('[data-cart-open]')) return;
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      };
      ['pointerover', 'touchstart', 'focusin'].forEach((type) => document.addEventListener(type, warm, { passive: true }));
      // The offer reconciler fixes gift lines after cart changes and announces it.
      addEventListener('cart:refresh', () => this.refresh());
      // A page restored from the back/forward cache shows the cart as it was when the shopper left.
      addEventListener('pageshow', (e) => { if (e.persisted) this.revalidate(); });
      // Back/forward without that cache re-renders the page from the browser's HTTP cache, with an old cart.
      if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') this.revalidate();
      this.freshAt = Date.now();
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open(opener = document.activeElement) {
      this.opener = opener;
      this.inert = false;
      this.classList.add('is-open');
      Sheet.lock('cart', [this]);
      // Focus the sheet itself: screen readers land in the dialog, and no focus ring appears after a tap.
      setTimeout(() => this.querySelector('.cart-sheet__panel')?.focus({ preventScroll: true }), 60);
      // The server-rendered cart can be stale (a gift fix still in flight when the page loaded, another tab).
      // Check after the slide-in so the morph never competes with the animation.
      if (Date.now() - this.freshAt > 2000) setTimeout(() => this.revalidate(), 400);
    }

    // Background refresh that yields to anything the shopper is doing.
    async revalidate() {
      if (this.inFlight || this.pendingSteps.size) return;
      try {
        const html = (await (await nativeFetch(`${root}?sections=${SECTION}`)).json())[SECTION];
        if (html && !this.inFlight && !this.pendingSteps.size) this.refresh(html);
      } catch { /* offline: keep what is on screen */ }
    }

    close() {
      this.classList.remove('is-open');
      this.inert = true;
      Sheet.unlock('cart', () => this.opener?.focus?.({ preventScroll: true }));
    }

    // Fetch (or take) fresh section HTML and morph it in.
    async refresh(html) {
      if (!html) {
        const response = await nativeFetch(`${root}?sections=${SECTION}`);
        html = (await response.json())[SECTION];
      }
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      if (!next) return;
      const tiers = Object.fromEntries([...this.querySelectorAll('[data-pack]')].map((p) => [p.dataset.pack, Number(p.dataset.tier)]));
      const lineCounts = Object.fromEntries([...this.querySelectorAll('[data-pack]')].map((p) => [p.dataset.pack, p.querySelectorAll('.cart-pack__lines [data-line]').length]));
      const entering = [];
      const wasInert = this.inert;
      morph(this, next, entering);
      this.inert = wasInert;
      this.removing.forEach((sel) => this.querySelectorAll(`${sel}:not([data-leaving])`).forEach((el) => { el.hidden = true; }));
      // Lines still heading to a new quantity keep showing it until their own request lands.
      this.targets.forEach((target, line) => {
        if (!line.isConnected) return this.targets.delete(line);
        line.querySelector('output').textContent = target;
        line.querySelector('[data-line-step="-1"]').disabled = target <= 1;
        line.querySelector('[data-line-step="1"]').disabled = target >= (Number(line.dataset.max) || Infinity);
        line.classList.add('is-pending');
      });
      entering.forEach(grow);
      this.querySelectorAll('[data-pack]').forEach((pack) => {
        const before = tiers[pack.dataset.pack];
        // New hoodies in a pack: open it so the shopper sees them land, then fold it away to keep the cart tidy.
        const lines = pack.querySelectorAll('.cart-pack__lines [data-line]:not([data-leaving])').length;
        if (lines >= 2 && lines > (lineCounts[pack.dataset.pack] || 0)) this.reveal(pack);
        // Confetti marks the discount actually changing: the re-rendered pack is on a higher tier than before.
        if (before !== undefined && Number(pack.dataset.tier) > before) celebrate(pack.querySelector('.cart-pack__nudge'));
      });
      this.updateCount(Number(this.dataset.count));
      this.freshAt = Date.now();
      // Protection alone is not an order: drop it when the last product leaves the cart.
      if (this.dataset.orphanProtection) this.change(this.dataset.orphanProtection, 0);
    }

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        if (badge.textContent !== String(count) && count && !reduced.matches) badge.animate([{ transform: 'scale(1.35)' }, { transform: 'none' }], { duration: SHAPE, easing: EASE });
        badge.textContent = count;
        badge.hidden = !count;
      });
      document.querySelectorAll('[data-cart-open]').forEach((link) => link.setAttribute('aria-label', `Cart, ${count} items`));
    }

    // Loading state: on from the first tap until the last queued request has re-rendered the drawer.
    setBusy() {
      const busy = this.inFlight > 0 || this.pendingSteps.size > 0;
      if (busy === this.hasAttribute('aria-busy')) return;
      this.toggleAttribute('aria-busy', busy);
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = busy ? 'Updating cart…' : 'Cart updated';
    }

    // Cart requests run one at a time; each response carries the re-rendered section.
    // `path` may be a function returning [path, body] (or nothing to skip): it runs when the request's turn comes,
    // so it sees the cart as the requests before it left it.
    request(path, body) {
      this.inFlight = (this.inFlight || 0) + 1;
      this.setBusy();
      this.querySelectorAll('.cart-error').forEach((el) => el.remove());
      const run = async () => {
        try {
          const op = typeof path === 'function' ? path() : [path, body];
          if (!op) return null;
          const response = await nativeFetch(`${root}${op[0]}`, {
            method: 'POST',
            headers: { ...json, 'X-Cart-Drawer': '1' },
            body: JSON.stringify({ ...op[1], sections: SECTION }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
          await this.refresh(data.sections?.[SECTION]);
          return data;
        } catch (error) {
          await this.refresh();
          this.querySelector('.cart-sheet__foot')?.insertAdjacentHTML('afterbegin', `<p class="cart-error" role="alert">${error.message}</p>`);
        } finally {
          this.inFlight -= 1;
          this.setBusy();
        }
      };
      this.queue = this.queue.then(run, run);
      return this.queue;
    }

    change(key, quantity) { return this.request('cart/change.js', { id: key, quantity }); }

    // Hoodies for a pack, plus the gifts the next tier unlocks.
    packItems({ variant, quantity, offer, instance, gifts }) {
      const properties = { _cn_offer_id: offer, _cn_offer_instance: instance };
      const items = [{ id: Number(variant), quantity: Number(quantity), properties: { ...properties, _cn_offer_role: 'qualifying' } }];
      (gifts || '').split(',').filter(Boolean).forEach((gift) => {
        const [id, key] = gift.split(':');
        items.push({ id: Number(id), quantity: 1, properties: { ...properties, _cn_offer_role: 'gift', _cn_offer_gift: key } });
      });
      return { items: items.reverse() };
    }

    // Stepper taps update the number at once and settle into one request after a short pause.
    step(line, delta) {
      // Bundles cap their hoodies as a whole: count what every line in the pack is already heading to.
      const pack = line.closest('[data-pack-max]');
      const others = pack ? [...pack.querySelectorAll('.cart-pack__lines [data-line] output')].filter((o) => !line.contains(o)).reduce((n, o) => n + Number(o.textContent), 0) : 0;
      const max = pack ? Number(pack.dataset.packMax) - others : Number(line.dataset.max) || Infinity;
      const now = Number(line.querySelector('output').textContent);
      // + never goes past the cap (and never lowers a line that's already over it); − never goes below 1.
      const next = delta > 0 ? Math.min(now + delta, Math.max(now, max)) : now + delta;
      this.aim(line, Math.max(1, next), 280);
      this.capPack(pack);
    }

    // A capped pack at its limit: no + on any of its lines.
    capPack(pack) {
      if (!pack) return;
      const outputs = [...pack.querySelectorAll('.cart-pack__lines [data-line] output')];
      const full = outputs.reduce((n, o) => n + Number(o.textContent), 0) >= Number(pack.dataset.packMax);
      outputs.forEach((o) => { o.closest('[data-line]').querySelector('[data-line-step="1"]').disabled = full; });
    }

    // Show a line's new quantity now and send it after `wait` ms. The amount to add is worked out when the
    // request runs, against the quantity the server last confirmed, so queued taps never add twice.
    aim(line, target, wait = 0) {
      line.querySelector('output').textContent = target;
      line.querySelector('[data-line-step="-1"]').disabled = target <= 1;
      line.querySelector('[data-line-step="1"]').disabled = target >= (Number(line.dataset.max) || Infinity);
      line.classList.add('is-pending');
      this.targets.set(line, target);
      clearTimeout(this.pendingSteps.get(line)?.timer);
      const fire = () => {
        if (this.pendingSteps.get(line)?.fire !== fire) return;
        this.pendingSteps.delete(line);
        this.request(() => {
          if (!line.isConnected || 'leaving' in line.dataset) return null; // removed meanwhile
          if (this.targets.get(line) === target) this.targets.delete(line);
          const added = target - Number(line.dataset.quantity);
          if (!added) return null;
          const pack = line.dataset.linePack && this.querySelector(`[data-pack="${CSS.escape(line.dataset.linePack)}"]`);
          const nudge = pack?.querySelector('[data-pack-add]');
          // Reaching the next tier also adds that tier's gifts.
          if (nudge && added >= Number(nudge.dataset.quantity)) {
            return ['cart/add.js', this.packItems({ ...nudge.dataset, variant: line.dataset.variant, quantity: added })];
          }
          return ['cart/change.js', { id: line.dataset.line, quantity: target }];
        });
      };
      this.pendingSteps.set(line, { timer: setTimeout(fire, wait), fire });
      this.setBusy();
    }

    // Fold or unfold a pack's hoodie list.
    fold(pack, folded) {
      pack.classList.toggle('is-folded', folded);
      pack.querySelector('[data-pack-toggle]')?.setAttribute('aria-expanded', !folded);
      pack.querySelector('.cart-pack__lines').inert = folded;
    }

    // Show a pack's hoodies for a second (after the drawer has slid in), then fold them back.
    reveal(pack) {
      if (!pack.querySelector('[data-pack-toggle]')) return;
      this.fold(pack, false);
      clearTimeout(this.folds.get(pack));
      this.folds.set(pack, setTimeout(() => {
        if (pack.isConnected && !pack.contains(document.activeElement)) this.fold(pack, true);
      }, 1000 + (reduced.matches ? 0 : 400)));
    }

    // Send any stepper still waiting out its pause.
    flushSteps() {
      [...this.pendingSteps.values()].forEach(({ timer, fire }) => { clearTimeout(timer); fire(); });
    }

    // Remove a row by its stable data-key: the line key is read from the newest copy of the row when the request runs.
    removeRow(rowKey, fallbackKey) {
      const sel = `[data-key="${CSS.escape(rowKey)}"]`;
      this.removing.add(sel);
      return this.request(() => {
        const row = [...this.querySelectorAll(sel)].find((r) => !('leaving' in r.dataset));
        return ['cart/change.js', { id: row?.dataset.line || fallbackKey, quantity: 0 }];
      }).then(() => this.unhide(sel));
    }

    // Remove a whole pack (its hoodies and gifts) in one request, with keys read when the request runs.
    removePack(pack) {
      const sel = `[data-pack="${CSS.escape(pack.dataset.pack)}"]`;
      const keysOf = (p) => [...p.querySelectorAll('[data-line]')].map((l) => l.dataset.line).concat([...p.querySelectorAll('[data-gift-line]')].map((g) => g.dataset.giftLine));
      const fallback = keysOf(pack);
      pack.querySelectorAll('[data-line]').forEach((line) => { clearTimeout(this.pendingSteps.get(line)?.timer); this.pendingSteps.delete(line); this.targets.delete(line); });
      this.removing.add(sel);
      collapse(pack);
      return this.request(() => {
        const fresh = [...this.querySelectorAll(sel)].find((p) => !('leaving' in p.dataset));
        const keys = fresh ? keysOf(fresh) : fallback;
        return ['cart/update.js', { updates: Object.fromEntries(keys.map((k) => [k, 0])) }];
      }).then(() => this.unhide(sel));
    }

    // A removal finished: stop hiding its target (it only still exists if the removal failed).
    unhide(sel) {
      this.removing.delete(sel);
      this.querySelectorAll(`${sel}[hidden]`).forEach((el) => { el.hidden = false; });
    }

    // Checkout (click or Enter) waits for pending cart changes, then goes ahead with the cart the shopper sees.
    onSubmit(e) {
      if (!this.inFlight && !this.pendingSteps.size) return;
      e.preventDefault();
      if (this.checkoutQueued) return;
      this.checkoutQueued = true;
      const form = e.target;
      const submitter = e.submitter;
      this.flushSteps();
      this.queue.then(() => {
        this.checkoutQueued = false;
        if (!this.querySelector('.cart-error') && form.isConnected) form.requestSubmit(submitter?.isConnected ? submitter : undefined);
      });
    }

    onClick(e) {
      const target = e.target.closest('button, [data-cart-close]');
      if (!target) return;
      if (target.matches('[data-cart-close]')) return this.close();
      if (target.matches('[data-line-step]')) return this.step(target.closest('[data-line]'), Number(target.dataset.lineStep));
      if (target.matches('[data-line-remove]')) {
        const line = target.closest('[data-line]');
        if (!line) return this.change(target.dataset.lineRemove, 0);
        clearTimeout(this.pendingSteps.get(line)?.timer);
        this.pendingSteps.delete(line);
        this.targets.delete(line);
        // The pack's last hoodie: its free gifts go in the same request, and the whole card folds away at once.
        const pack = line.closest('[data-pack]');
        if (pack && !pack.querySelector(`.cart-pack__lines [data-line]:not([data-leaving]):not([data-key="${CSS.escape(line.dataset.key)}"])`)) return this.removePack(pack);
        collapse(line); // queued quantity changes for a leaving line are skipped
        // Shopify changes a line's key when its discounts change (a tier-up still in flight), so look the key up
        // from the row's stable identity when this request's turn comes.
        return this.removeRow(line.dataset.key, target.dataset.lineRemove);
      }
      if (target.matches('[data-gift-remove]')) {
        // Remember the gift so it can be added back. Underscore cart attributes stay out of checkout.
        const list = (this.dataset.removedGifts || '').split(',').filter(Boolean);
        if (!list.includes(target.dataset.gift)) list.push(target.dataset.gift);
        const item = target.closest('li');
        item.classList.add('is-removed');
        const pack = item.closest('[data-pack]')?.dataset.pack;
        const find = () => [...this.querySelectorAll(`[data-pack="${CSS.escape(pack)}"] li[data-key="${CSS.escape(item.dataset.key)}"] [data-gift-remove]`)][0];
        return this.request(() => ['cart/update.js', { updates: { [find()?.dataset.giftRemove || target.dataset.giftRemove]: 0 }, attributes: { _removed_gifts: list.join(',') } }]);
      }
      if (target.matches('[data-gift-add]')) {
        target.classList.add('is-pending');
        const [instance, key, variant] = target.dataset.giftAdd.split('|');
        const list = (this.dataset.removedGifts || '').split(',').filter((entry) => entry && entry !== target.dataset.giftAdd);
        target.closest('li').classList.remove('is-removed');
        this.request('cart/update.js', { attributes: { _removed_gifts: list.join(',') } });
        return this.request('cart/add.js', { items: [{ id: Number(variant), quantity: 1, properties: { _cn_offer_id: target.dataset.offer, _cn_offer_instance: instance, _cn_offer_role: 'gift', _cn_offer_gift: key } }] });
      }
      if (target.matches('[data-pack-add]')) {
        // Show the new count at once; the confetti waits for the server to apply the new tier.
        const pack = target.closest('[data-pack]');
        const line = pack.querySelector(`[data-variant="${target.dataset.packAdd}"]:not([data-leaving])`);
        target.classList.add('is-pending');
        target.disabled = true; // one tier at a time; the re-render brings the next nudge
        if (line) return this.aim(line, Number(line.querySelector('output').textContent) + Number(target.dataset.quantity));
        return this.request('cart/add.js', this.packItems({ ...target.dataset, variant: target.dataset.packAdd }));
      }
      if (target.matches('[data-variant-add]')) {
        target.classList.add('is-pending');
        return this.request('cart/add.js', { items: [{ id: Number(target.dataset.variantAdd), quantity: 1 }] });
      }
      if (target.matches('[data-pack-toggle]')) {
        const pack = target.closest('[data-pack]');
        clearTimeout(this.folds.get(pack));
        return this.fold(pack, !pack.classList.contains('is-folded'));
      }
      if (target.matches('[data-gifts-toggle]')) {
        const gifts = target.closest('[data-gifts]');
        const open = !gifts.classList.contains('is-expanded');
        gifts.classList.toggle('is-expanded', open);
        target.setAttribute('aria-expanded', open);
        gifts.querySelector('.collapsible').inert = !open;
        return;
      }
      if (target.matches('[data-protect-more]')) {
        const tip = this.querySelector('#protect-tip');
        tip.hidden = !tip.hidden;
        target.setAttribute('aria-expanded', !tip.hidden);
      }
    }

    // Shipping protection switch adds or removes the protection product line.
    onChange(e) {
      const input = e.target.closest('[data-protect]');
      if (!input) return;
      if (input.checked) this.request('cart/add.js', { items: [{ id: Number(input.dataset.protect), quantity: 1 }] });
      else this.change(input.dataset.protectLine, 0);
    }

    // Dawn product forms talk to a <cart-drawer>; these three methods keep them working.
    getSectionsToRender() { return [{ id: SECTION, section: SECTION }]; }
    setActiveElement(element) { this.opener = element; }
    renderContents(response) {
      this.refresh(response?.sections?.[SECTION]).then(() => this.open());
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const drawer = () => document.querySelector('cart-drawer');

  // Public API for product pages and landing pages.
  window.Cart = {
    open: () => drawer()?.open(),
    close: () => drawer()?.close(),
    refresh: () => drawer()?.refresh(),
  };

  // Any add-to-cart elsewhere on the page refreshes and opens the drawer; other cart changes refresh it.
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    const headers = new Headers(args[1]?.headers || {});
    const cartCall = url.match(/\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/);
    if (!cartCall || !response.ok || headers.get('X-Cart-Drawer') || headers.get('X-Cart-Silent')) return response;
    const sectionsRequested = /sections=/.test(String(args[1]?.body || '')) || args[1]?.body instanceof FormData;
    if (!sectionsRequested) drawer()?.refresh().then(() => { if (cartCall[1] === 'add') drawer()?.open(); });
    return response;
  };
})();
